/** Offre ACACED : 6 mois Premium offerts si diplôme obtenu dans les 6 derniers mois (France). */

export const ACACED_PAGE_PATH = '/acaced';
export const ACACED_APP_PATH = '/app/acaced';
/** Création de compte avec mémorisation de l'intention (redirige ensuite vers l'espace ACACED). */
export const ACACED_SIGNUP_PATH = '/signup-user?source=acaced';

export const ACACED_REWARD_MONTHS = 6;
export const ACACED_REVIEW_DELAY = '48 h ouvrées';
export const ACACED_MAX_FILE_MB = 10;

/** Condition d'éligibilité — reprise partout (bandeau, hero, teaser, conditions). */
export const ACACED_ELIGIBILITY =
  "Si tu as obtenu l'ACACED dans les 6 derniers mois";

export const ACACED_BANNER_MESSAGE = `${ACACED_ELIGIBILITY} : ${ACACED_REWARD_MONTHS} mois de Rintintin Premium offerts`;
export const ACACED_BANNER_CTA_LABEL = 'Envoyer mon diplôme';

export const ACACED_HERO_BADGE = 'ACACED obtenu dans les 6 derniers mois';
export const ACACED_HERO_TITLE = `${ACACED_REWARD_MONTHS} mois de Premium offerts`;
export const ACACED_HERO_TITLE_ACCENT = "si tu as obtenu l'ACACED dans les 6 derniers mois.";
export const ACACED_HERO_SUBTITLE =
  `${ACACED_ELIGIBILITY}, envoie-nous ton diplôme (PDF ou photo). Dès que notre équipe l'a vérifié, tu profites de Rintintin Premium pendant 6 mois, sans carte bancaire.`;

export const ACACED_HERO_PILLS = [
  { label: 'Offerts', value: '6 mois' },
  { label: 'Vérification', value: ACACED_REVIEW_DELAY },
  { label: 'Carte bancaire', value: 'Non requise' },
] as const;

export const ACACED_SIGNUP_CTA_LABEL = 'Créer mon compte et envoyer mon diplôme';
export const ACACED_APP_CTA_LABEL = 'Envoyer mon diplôme';

export const ACACED_SEO_TITLE =
  "ACACED (obtenu dans les 6 derniers mois) : 6 mois de Rintintin Pro Premium offerts";
export const ACACED_SEO_DESCRIPTION =
  `${ACACED_ELIGIBILITY}, envoie ton diplôme à Rintintin (PDF ou photo) : après vérification, 6 mois de Premium offerts, sans carte bancaire. Offre pour les pros en France.`;

export const ACACED_STEPS_TITLE = 'Comment ça marche ?';

export interface AcacedStep {
  number: string;
  title: string;
  description: string;
}

export const ACACED_STEPS: AcacedStep[] = [
  {
    number: '01',
    title: 'Crée ton compte pro',
    description:
      'Inscription gratuite en quelques minutes. Tu as déjà un compte ? Connecte-toi et va directement à l\'étape 2.',
  },
  {
    number: '02',
    title: 'Envoie ton diplôme',
    description: `Depuis ton espace, dépose ton diplôme ACACED (obtenu dans les 6 derniers mois) en PDF ou en photo (${ACACED_MAX_FILE_MB} Mo max). Il reste privé : seule notre équipe peut le consulter.`,
  },
  {
    number: '03',
    title: `Profite de ${ACACED_REWARD_MONTHS} mois de Premium`,
    description: `On vérifie ton document sous ${ACACED_REVIEW_DELAY}. Dès la validation, Premium s'active sur ton compte et tu reçois un email de confirmation.`,
  },
];

export interface AcacedBenefit {
  icon: string;
  title: string;
  description: string;
}

export const ACACED_BENEFITS_TITLE = 'Ce que tu débloques avec Premium';
export const ACACED_BENEFITS_SUBTITLE =
  'Tout ce qui fait gagner du temps au quotidien, offert pendant 6 mois.';

export const ACACED_BENEFITS: AcacedBenefit[] = [
  {
    icon: '📅',
    title: 'Planning & réservations',
    description: 'Tes clients réservent et paient en ligne, ton agenda reste à jour tout seul.',
  },
  {
    icon: '🧾',
    title: 'Facturation & e-facturation',
    description: 'Factures, paiements Stripe, exports compta et facturation électronique au même endroit.',
  },
  {
    icon: '💬',
    title: 'Messagerie & rappels',
    description: 'Échanges clients, rappels SMS et emails automatiques pour limiter les oublis.',
  },
  {
    icon: '🐕',
    title: 'Suivi des clients et des chiens',
    description: 'Fiches, carnets de santé, documents et historique de chaque animal.',
  },
];

export const ACACED_CONDITIONS_TITLE = 'Conditions de l\'offre';
export const ACACED_CONDITIONS: string[] = [
  `${ACACED_ELIGIBILITY} — offre réservée aux professionnels établis en France.`,
  'Une seule fois par compte professionnel, après validation manuelle du diplôme par notre équipe (la date d\'obtention doit figurer sur le document).',
  'Réservée aux comptes qui ne sont pas déjà abonnés Premium au moment de la validation. Si tu es déjà abonné, ne t\'inquiète pas : ton diplôme est quand même enregistré.',
  `Les ${ACACED_REWARD_MONTHS} mois s'activent sans carte bancaire. À la fin, ton compte repasse automatiquement en version gratuite, sauf si tu choisis de continuer.`,
  'Ton diplôme est un document personnel : il est stocké de façon privée et utilisé uniquement pour cette vérification.',
];

export const ACACED_FAQ_SUBTITLE = 'Tout ce qu\'il faut savoir avant d\'envoyer ton diplôme.';

export const ACACED_FAQ: Array<{ question: string; answer: string }> = [
  {
    question: 'Qui peut bénéficier de l\'offre ?',
    answer:
      `${ACACED_ELIGIBILITY} et que tu es un professionnel établi en France. La date d'obtention doit être lisible sur ton document.`,
  },
  {
    question: 'Qu\'est-ce que le diplôme ACACED ?',
    answer:
      'L\'ACACED (attestation de connaissances pour les animaux de compagnie d\'espèces domestiques) est exigée pour exercer certaines activités auprès des chiens et chats : éducation, garde, pension, toilettage, vente…',
  },
  {
    question: 'Quels documents sont acceptés ?',
    answer: `Un PDF, une photo JPEG ou PNG (ou HEIC depuis un iPhone) de ton attestation ou diplôme. ${ACACED_MAX_FILE_MB} Mo maximum. Le texte doit être lisible : nom, intitulé et date d'obtention.`,
  },
  {
    question: 'Combien de temps pour la validation ?',
    answer: `Notre équipe vérifie chaque diplôme manuellement, en général sous ${ACACED_REVIEW_DELAY}. Tu reçois un email dès que c'est fait, ou si on a besoin d'un autre document.`,
  },
  {
    question: 'Dois-je donner ma carte bancaire ?',
    answer:
      'Non. Les 6 mois Premium s\'activent sans carte. Rien n\'est prélevé à la fin : ton compte repasse simplement en version gratuite si tu ne souhaites pas continuer.',
  },
  {
    question: 'J\'ai déjà un compte Rintintin, je peux en profiter ?',
    answer:
      `Oui. ${ACACED_ELIGIBILITY} : connecte-toi, ouvre « Diplôme ACACED » dans ton espace et envoie ton document. Si tu es déjà abonné Premium, l'offre ne s'applique pas, mais ton diplôme reste enregistré.`,
  },
  {
    question: 'Que devient mon diplôme ?',
    answer:
      'Il est stocké de façon privée, accessible uniquement à l\'équipe Rintintin pour la vérification, et jamais affiché publiquement.',
  },
  {
    question: 'L\'offre est-elle disponible en Belgique ?',
    answer: 'Pas pour l\'instant : l\'ACACED est un diplôme français, l\'offre est réservée aux pros établis en France.',
  },
];

export const ACACED_CTA_TITLE = 'Prêt à envoyer ton diplôme ?';
export const ACACED_CTA_SUBTITLE = `${ACACED_ELIGIBILITY}, crée ton compte et dépose-le : ${ACACED_REWARD_MONTHS} mois de Premium t'attendent.`;

export const ACACED_LEGAL_DISCLAIMER =
  'Offre réservée aux diplômes ACACED obtenus dans les 6 derniers mois, soumise à validation manuelle et à conditions (voir ci-dessus). Rintintin se réserve le droit de refuser un document illisible, incomplet, hors délai ou non conforme.';

/** Teaser landing / pages métier. */
export const ACACED_TEASER_TITLE = `${ACACED_ELIGIBILITY} : ${ACACED_REWARD_MONTHS} mois de Premium offerts`;
export const ACACED_TEASER_TEXT =
  'Envoie ton diplôme (PDF ou photo), on le vérifie sous 48 h : Premium s\'active sans carte bancaire.';
export const ACACED_TEASER_CTA_LABEL = 'Découvrir l\'offre';

/** Métiers pour lesquels l'ACACED est pertinent (teaser sur les pages métier). */
export const ACACED_TEASER_METIER_PATHS = [
  '/educateur-canin',
  '/pet-sitter',
  '/pension-canine',
  '/promeneur-canin',
  '/toiletteur',
  '/comportementaliste',
] as const;

/** Mémorisation de l'intention d'inscription ACACED (survit à la redirection Google OAuth). */
export const ACACED_INTENT_STORAGE_KEY = 'rintintin:acaced-signup-intent';
export const ACACED_INTENT_TTL_MS = 24 * 60 * 60 * 1000;
